import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { RiCalendarCheckLine, RiCloseLine } from 'react-icons/ri';
import {
  useAddObligationFiscaleMutation,
  useUpdateObligationFiscaleMutation,
} from 'actions/fiscalite/mutations';
import { toastApiErrors } from 'utils/toastApiError';

if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

const ORGANISMES = [
  { value: 'DGID', label: 'DGID (Impôts)' },
  { value: 'IPRES', label: 'IPRES' },
  { value: 'CSS', label: 'CSS (Sécurité Sociale)' },
  { value: 'TRESOR', label: 'Trésor Public' },
  { value: 'AUTRE', label: 'Autre organisme' },
];

const PERIODICITES = [
  { value: 'MENSUELLE', label: 'Mensuelle' },
  { value: 'TRIMESTRIELLE', label: 'Trimestrielle' },
  { value: 'ANNUELLE', label: 'Annuelle' },
  { value: 'PONCTUELLE', label: 'Ponctuelle' },
];

const FORM_VIDE = {
  nom: '',
  code: '',
  organisme: 'DGID',
  periodicite: 'MENSUELLE',
  periode: '',
  date_echeance: '',
  montant: '',
  compte_charge: '',
  description: '',
  risque_penalite: '',
  is_recurrente: false,
};

/**
 * Modal de création / édition d'une échéance fiscale personnalisée.
 * En édition, `obligation` est fournie ; sinon création.
 */
const ModalObligationForm = ({ isOpen, onClose, dossierId, obligation }) => {
  const [form, setForm] = useState(FORM_VIDE);
  const [addObligation, { isLoading: isAdding }] = useAddObligationFiscaleMutation();
  const [updateObligation, { isLoading: isUpdating }] =
    useUpdateObligationFiscaleMutation();
  const isLoading = isAdding || isUpdating;
  const isEdit = Boolean(obligation?.id);

  useEffect(() => {
    if (isOpen) {
      setForm(
        isEdit
          ? {
              nom: obligation.nom || '',
              code: obligation.code || '',
              organisme: obligation.organisme || 'DGID',
              periodicite: obligation.periodicite || 'MENSUELLE',
              periode: obligation.periode || '',
              date_echeance: obligation.date_echeance || '',
              montant: obligation.montant ?? '',
              compte_charge: obligation.compte_charge || '',
              description: obligation.description || '',
              risque_penalite: obligation.risque_penalite || '',
              is_recurrente: Boolean(obligation.is_recurrente),
            }
          : FORM_VIDE
      );
    }
  }, [isOpen, isEdit, obligation]);

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return toast.warning('Le nom de l\'obligation est requis.');
    if (!form.date_echeance) return toast.warning('La date d\'échéance est requise.');

    const payload = {
      dossier_id: dossierId,
      dossier: dossierId,
      ...form,
      montant: form.montant === '' ? 0 : Number(form.montant),
    };

    try {
      if (isEdit) {
        await updateObligation({ id: obligation.id, ...payload }).unwrap();
        toast.success('Échéance fiscale mise à jour.');
      } else {
        await addObligation(payload).unwrap();
        toast.success('Échéance fiscale ajoutée.');
      }
      onClose();
    } catch (error) {
      toastApiErrors(error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Échéance fiscale"
      className="Modal fisc-modal fisc-modal-form"
      overlayClassName="Overlay"
    >
      <div className="fisc-modal-header">
        <div className="fisc-modal-icon">
          <RiCalendarCheckLine size={20} />
        </div>
        <div className="fisc-modal-titles">
          <h2>{isEdit ? "Modifier l'échéance" : 'Ajouter une échéance fiscale'}</h2>
          <p>
            {isEdit
              ? "Mettre à jour les informations de l'obligation"
              : 'Définir une obligation fiscale ou sociale à suivre'}
          </p>
        </div>
        <button
          type="button"
          className="fisc-modal-close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <RiCloseLine size={18} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="fisc-modal-content">
        <div className="fisc-modal-body">
        <div className="form-group">
          <label className="fw-bold small">Nom de la taxe / obligation *</label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex : Impôt sur les revenus locatifs, Taxe véhicule..."
            value={form.nom}
            onChange={set('nom')}
            required
          />
        </div>

        <div className="row mt-2">
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Code / référence</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex : DGID-VRS"
              value={form.code}
              onChange={set('code')}
            />
          </div>
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Organisme collecteur</label>
            <select
              className="form-control"
              value={form.organisme}
              onChange={set('organisme')}
            >
              {ORGANISMES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Périodicité</label>
            <select
              className="form-control"
              value={form.periodicite}
              onChange={set('periodicite')}
            >
              {PERIODICITES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Période d&rsquo;imposition</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex : Novembre 2026, T4 2026..."
              value={form.periode}
              onChange={set('periode')}
            />
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Montant exigible (FCFA)</label>
            <input
              type="number"
              min="0"
              className="form-control"
              value={form.montant}
              onChange={set('montant')}
            />
          </div>
          <div className="col-md-6 form-group">
            <label className="fw-bold small">Date butoir *</label>
            <input
              type="date"
              className="form-control"
              value={form.date_echeance}
              onChange={set('date_echeance')}
              required
            />
          </div>
        </div>

        <div className="form-group mt-2">
          <label className="fw-bold small">
            Compte comptable débité au paiement (ex : 646000, 447000, 443000)
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Par défaut : 646000 (impôts) ou 431000 (social)"
            value={form.compte_charge}
            onChange={set('compte_charge')}
          />
        </div>

        <div className="form-group mt-2">
          <label className="fw-bold small">Description</label>
          <textarea
            className="form-control"
            rows="2"
            value={form.description}
            onChange={set('description')}
          />
        </div>

        <div className="form-group mt-2">
          <label className="fw-bold small">Pénalité encourue en cas de retard</label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex : Majoration de 25 % + intérêts de retard"
            value={form.risque_penalite}
            onChange={set('risque_penalite')}
          />
        </div>

        <div className="form-check mt-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="fisc-recurrente"
            checked={form.is_recurrente}
            onChange={set('is_recurrente')}
          />
          <label className="form-check-label" htmlFor="fisc-recurrente">
            Échéance récurrente : régénérer automatiquement à la période suivante
            après paiement
          </label>
        </div>

        </div>

        <div className="fisc-modal-footer">
          <button
            type="button"
            className="fisc-btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="fisc-btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : isEdit ? 'Enregistrer' : "Ajouter l'échéance"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ModalObligationForm;
